import React, { useState, useEffect } from 'react';
import { doc, setDoc, getDoc, collection, getDocs, query, orderBy, where } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { 
  FaWeight, 
  FaUtensils, 
  FaSave, 
  FaPlus, 
  FaTint, 
  FaClipboardList, 
  FaChartLine, 
  FaNotesMedical, 
  FaCalendarDay, 
  FaArrowLeft
} from 'react-icons/fa';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

const PageContainer = styled.div`
  padding: 1rem;
  max-width: 1000px;
  margin: 0 auto;
  background: ${({ theme }) => theme.colors?.background || '#f8f9fa'};
  min-height: 100vh;
  
  @media (min-width: 768px) {
    padding: 2rem;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
  gap: 0.75rem;
  
  @media (min-width: 768px) {
    margin-bottom: 2rem;
    gap: 1rem;
  }
`;

const BackButton = styled.button`
  background: ${({ theme }) => theme.colors?.primary || '#291759'};
  border: none;
  color: white;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(0,0,0,0.15);
  }
  
  @media (min-width: 768px) {
    width: 40px;
    height: 40px;
  }
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 1.6rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors?.textPrimary || '#333'};
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -6px;
    left: 0;
    width: 50px;
    height: 3px;
    background: ${({ theme }) => theme.colors?.primary || '#291759'};
    border-radius: 3px;
  }
  
  @media (min-width: 768px) {
    font-size: 2rem;
    
    &:after {
      bottom: -8px;
      width: 60px;
    }
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.08);
  padding: 1rem;
  margin-bottom: 1.5rem;
  transition: transform 0.3s, box-shadow 0.3s;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 24px rgba(0,0,0,0.12);
  }
  
  @media (min-width: 768px) {
    padding: 2rem;
    margin-bottom: 2rem;
  }
`;

const PlanCard = styled(Card)`
  border-left: 5px solid ${({ theme }) => theme.colors?.primary || '#291759'};
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.25rem;
  gap: 0.75rem;
  
  @media (min-width: 768px) {
    margin-bottom: 1.5rem;
  }
`;

const CardIcon = styled.div`
  background: ${({ bgColor }) => bgColor || 'rgba(41, 23, 89, 0.1)'};
  color: ${({ color, theme }) => color || theme?.colors?.primary || '#291759'};
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
  
  @media (min-width: 768px) {
    width: 48px;
    height: 48px;
    font-size: 1.4rem;
  }
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors?.textPrimary || '#333'};
  
  @media (min-width: 768px) {
    font-size: 1.4rem;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.25rem;
  
  @media (min-width: 768px) {
    margin-bottom: 1.5rem;
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.75rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors?.textSecondary || '#555'};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 0.95rem;
  background: #f9fafc;
  transition: all 0.3s;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors?.primary || '#291759'};
    box-shadow: 0 0 0 3px rgba(41, 23, 89, 0.15);
    background: white;
  }
  
  @media (min-width: 768px) {
    padding: 0.85rem;
    font-size: 1rem;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 0.95rem;
  min-height: 80px;
  resize: vertical;
  background: #f9fafc;
  transition: all 0.3s;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors?.primary || '#291759'};
    box-shadow: 0 0 0 3px rgba(41, 23, 89, 0.15);
    background: white;
  }
  
  @media (min-width: 768px) {
    padding: 0.85rem;
    font-size: 1rem;
    min-height: 100px;
  }
`;

const Button = styled.button`
  background: ${({ theme }) => theme.colors?.primary || '#291759'};
  color: white;
  border: none;
  padding: 0.85rem 1.5rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.3s;
  box-shadow: 0 4px 8px rgba(41, 23, 89, 0.25);
  width: 100%;
  
  &:hover {
    background: ${({ theme }) => theme.colors?.primaryDark || '#1e0f45'};
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(41, 23, 89, 0.3);
  }
  
  &:disabled {
    background: #a0a0a0;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  @media (min-width: 768px) {
    padding: 1rem 2rem;
    font-size: 1.1rem;
    gap: 0.75rem;
    width: auto;
  }
`;

const MealSection = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  background: #f9fafc;
  transition: all 0.3s;
  
  &:hover {
    border-color: ${({ theme }) => theme.colors?.primary || '#291759'};
    box-shadow: 0 4px 12px rgba(0,0,0,0.05);
  }
`;

const MealHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  gap: 0.75rem;
`;

const MealIcon = styled.div`
  background: ${({ theme }) => theme.colors?.primaryLight || 'rgba(41, 23, 89, 0.1)'};
  color: ${({ theme }) => theme.colors?.primary || '#291759'};
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
`;

const MealTitle = styled.h4`
  margin: 0;
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors?.primary || '#291759'};
`;

const SuccessMessage = styled.div`
  background: #d4edda;
  color: #155724;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);
  
  svg {
    font-size: 1.5rem;
  }
`;

const ErrorMessage = styled.div`
  background: #f8d7da;
  color: #721c24;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);
  
  svg {
    font-size: 1.5rem;
  }
`;

const DateInput = styled(Input)`
  max-width: 200px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 12px;
  background: #e0e0e0;
  border-radius: 6px;
  overflow: hidden;
  margin: 1rem 0;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${props => props.progress}%;
  background: linear-gradient(90deg, 
    ${({ theme }) => theme.colors?.primary || '#291759'}, 
    ${({ theme }) => theme.colors?.accent || '#858CD3'}
  );
  border-radius: 6px;
  transition: width 0.5s ease;
`;

const DailyProgressTracker = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [existingProgress, setExistingProgress] = useState(null);
  const [userPlan, setUserPlan] = useState(null);
  const [userPlanProgress, setUserPlanProgress] = useState(0);
  const navigate = useNavigate();
  
  const [progressData, setProgressData] = useState({
    date: new Date().toISOString().split('T')[0],
    weight: '',
    meals: {
      breakfast: {
        foods: '',
        calories: '',
        notes: ''
      },
      lunch: {
        foods: '',
        calories: '',
        notes: ''
      },
      dinner: {
        foods: '',
        calories: '',
        notes: ''
      }
    },
    totalCalories: 0,
    waterIntake: '',
    notes: ''
  });

  useEffect(() => {
    loadProgressForDate(selectedDate);
    fetchUserPlan();
  }, [selectedDate, currentUser]);

  useEffect(() => {
    // Calculate total calories whenever meal calories change
    const breakfast = parseInt(progressData.meals.breakfast.calories) || 0;
    const lunch = parseInt(progressData.meals.lunch.calories) || 0;
    const dinner = parseInt(progressData.meals.dinner.calories) || 0;
    const total = breakfast + lunch + dinner;
    
    setProgressData(prev => ({
      ...prev,
      totalCalories: total
    }));
  }, [progressData.meals.breakfast.calories, progressData.meals.lunch.calories, progressData.meals.dinner.calories]);

  const fetchUserPlan = async () => {
    if (!currentUser) return;
    
    try {
      // First get user document to find the selected plan ID
      const userRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const selectedPlanId = userData.selectedPlanId;
        
        if (selectedPlanId) {
          // Get plan details
          const planRef = doc(db, 'plans', selectedPlanId);
          const planDoc = await getDoc(planRef);
          
          if (planDoc.exists()) {
            const planData = planDoc.data();
            setUserPlan({
              ...planData,
              id: selectedPlanId,
              selectedAt: userData.planSelectedAt || null
            });
            
            // Get plan progress
            const progressRef = doc(db, 'users', currentUser.uid, 'planProgress', 'current');
            const progressDoc = await getDoc(progressRef);
            
            if (progressDoc.exists()) {
              const progressData = progressDoc.data();
              setUserPlanProgress(progressData.progressPercentage || 0);
            }
          }
        }
      }
    } catch (err) {
      console.error('Error fetching user plan:', err);
    }
  };

  const loadProgressForDate = async (date) => {
    if (!currentUser) return;
    
    try {
      const progressRef = doc(db, 'users', currentUser.uid, 'dailyProgress', date);
      const progressDoc = await getDoc(progressRef);
      
      if (progressDoc.exists()) {
        const data = progressDoc.data();
        setExistingProgress(data);
        setProgressData({
          date: date,
          weight: data.weight || '',
          meals: data.meals || {
            breakfast: { foods: '', calories: '', notes: '' },
            lunch: { foods: '', calories: '', notes: '' },
            dinner: { foods: '', calories: '', notes: '' }
          },
          totalCalories: data.totalCalories || 0,
          waterIntake: data.waterIntake || '',
          notes: data.notes || ''
        });
      } else {
        setExistingProgress(null);
        setProgressData({
          date: date,
          weight: '',
          meals: {
            breakfast: { foods: '', calories: '', notes: '' },
            lunch: { foods: '', calories: '', notes: '' },
            dinner: { foods: '', calories: '', notes: '' }
          },
          totalCalories: 0,
          waterIntake: '',
          notes: ''
        });
      }
    } catch (err) {
      console.error('Error loading progress:', err);
      setError('Failed to load progress data');
    }
  };

  // Function to update health profile weight
  const updateHealthProfileWeight = async (newWeight) => {
    if (!currentUser || !newWeight) return;
    
    try {
      const healthRef = doc(db, 'users', currentUser.uid, 'health', 'profile');
      await setDoc(healthRef, { 
        weight: parseFloat(newWeight),
        updatedAt: new Date()
      }, { merge: true });
      
      console.log('Health profile weight updated successfully');
    } catch (err) {
      console.error('Error updating health profile weight:', err);
      // Don't throw error to avoid interrupting the main save operation
    }
  };

  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setSelectedDate(newDate);
    setProgressData(prev => ({ ...prev, date: newDate }));
  };

  const handleInputChange = (field, value) => {
    setProgressData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleMealChange = (mealType, field, value) => {
    setProgressData(prev => ({
      ...prev,
      meals: {
        ...prev.meals,
        [mealType]: {
          ...prev.meals[mealType],
          [field]: value
        }
      }
    }));
  };

  const handleSubmit = async () => {
    if (!currentUser) {
      setError('You must be logged in to save progress');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const progressToSave = {
        ...progressData,
        userId: currentUser.uid,
        createdAt: existingProgress ? existingProgress.createdAt : new Date(),
        updatedAt: new Date()
      };

      // Save daily progress
      const progressRef = doc(db, 'users', currentUser.uid, 'dailyProgress', selectedDate);
      await setDoc(progressRef, progressToSave);

      // Update health profile weight if weight is provided
      if (progressData.weight && progressData.weight.trim() !== '') {
        await updateHealthProfileWeight(progressData.weight);
      }

      setSuccess('Daily progress saved successfully! Health profile weight updated.');
      setExistingProgress(progressToSave);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (err) {
      console.error('Error saving progress:', err);
      setError('Failed to save progress: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer>
      <Header>
        <BackButton onClick={() => navigate('/customer/dashboard')}>
          <FaArrowLeft />
        </BackButton>
        <PageTitle>Daily Progress Tracker</PageTitle>
      </Header>

      {/* Current Plan Section */}
      {userPlan && (
        <PlanCard>
          <CardHeader>
            <CardIcon>
              <FaClipboardList />
            </CardIcon>
            <CardTitle>Your Current Plan - {userPlan.name}</CardTitle>
          </CardHeader>
          <p>{userPlan.description}</p>
          
          <div>
            <div style={{display: 'flex', justifyContent: 'space-between'}}>
              <span style={{fontWeight: 500}}>Plan Progress</span>
              <span>{userPlanProgress.toFixed(0)}%</span>
            </div>
            <ProgressBar>
              <ProgressFill progress={userPlanProgress} />
            </ProgressBar>
            <div style={{display: 'flex', justifyContent: 'space-between', color: '#666', fontSize: '0.9rem'}}>
              <span>Day {Math.ceil(userPlanProgress * userPlan.duration / 100) || 1}</span>
              <span>{userPlan.duration || 30} Days Total</span>
            </div>
          </div>
        </PlanCard>
      )}

      {/* Date Selection */}
      <Card>
        <CardHeader>
          <CardIcon color="#0056b3" bgColor="rgba(0, 86, 179, 0.1)">
            <FaCalendarDay />
          </CardIcon>
          <CardTitle>Select Date</CardTitle>
        </CardHeader>
        
        <FormGroup>
          <DateInput
            type="date"
            id="date"
            value={selectedDate}
            onChange={handleDateChange}
            max={new Date().toISOString().split('T')[0]}
          />
          {existingProgress && (
            <div style={{ marginTop: '0.75rem', color: '#28a745', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <FaSave size={14} />
              <span>Progress data exists for this date</span>
            </div>
          )}
        </FormGroup>
      </Card>

      {/* Weight Tracking */}
      <Card>
        <CardHeader>
          <CardIcon color="#28a745" bgColor="rgba(40, 167, 69, 0.1)">
            <FaWeight />
          </CardIcon>
          <CardTitle>Weight Tracking</CardTitle>
        </CardHeader>
        
        <FormGroup>
          <Label htmlFor="weight">
            <FaWeight size={14} />
            Today's Weight (kg)
          </Label>
          <Input
            type="number"
            id="weight"
            value={progressData.weight}
            onChange={(e) => handleInputChange('weight', e.target.value)}
            step="0.1"
            min="30"
            max="300"
            placeholder="Enter your weight"
          />
        </FormGroup>
      </Card>

      {/* Meals Tracking */}
      <Card>
        <CardHeader>
          <CardIcon color="#fd7e14" bgColor="rgba(253, 126, 20, 0.1)">
            <FaUtensils />
          </CardIcon>
          <CardTitle>Meals & Nutrition</CardTitle>
        </CardHeader>
        
        <MealSection>
          <MealHeader>
            <MealIcon>
              <FaUtensils />
            </MealIcon>
            <MealTitle>Breakfast</MealTitle>
          </MealHeader>
          
          <FormGroup>
            <Label>Foods Eaten</Label>
            <TextArea
              value={progressData.meals.breakfast.foods}
              onChange={(e) => handleMealChange('breakfast', 'foods', e.target.value)}
              placeholder="What did you eat for breakfast?"
            />
          </FormGroup>
          
          <Grid>
            <FormGroup>
              <Label>Estimated Calories</Label>
              <Input
                type="number"
                value={progressData.meals.breakfast.calories}
                onChange={(e) => handleMealChange('breakfast', 'calories', e.target.value)}
                placeholder="Calories"
                min="0"
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Notes</Label>
              <Input
                type="text"
                value={progressData.meals.breakfast.notes}
                onChange={(e) => handleMealChange('breakfast', 'notes', e.target.value)}
                placeholder="Any notes?"
              />
            </FormGroup>
          </Grid>
        </MealSection>
        
        <MealSection>
          <MealHeader>
            <MealIcon>
              <FaUtensils />
            </MealIcon>
            <MealTitle>Lunch</MealTitle>
          </MealHeader>
          
          <FormGroup>
            <Label>Foods Eaten</Label>
            <TextArea
              value={progressData.meals.lunch.foods}
              onChange={(e) => handleMealChange('lunch', 'foods', e.target.value)}
              placeholder="What did you eat for lunch?"
            />
          </FormGroup>
          
          <Grid>
            <FormGroup>
              <Label>Estimated Calories</Label>
              <Input
                type="number"
                value={progressData.meals.lunch.calories}
                onChange={(e) => handleMealChange('lunch', 'calories', e.target.value)}
                placeholder="Calories"
                min="0"
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Notes</Label>
              <Input
                type="text"
                value={progressData.meals.lunch.notes}
                onChange={(e) => handleMealChange('lunch', 'notes', e.target.value)}
                placeholder="Any notes?"
              />
            </FormGroup>
          </Grid>
        </MealSection>
        
        <MealSection>
          <MealHeader>
            <MealIcon>
              <FaUtensils />
            </MealIcon>
            <MealTitle>Dinner</MealTitle>
          </MealHeader>
          
          <FormGroup>
            <Label>Foods Eaten</Label>
            <TextArea
              value={progressData.meals.dinner.foods}
              onChange={(e) => handleMealChange('dinner', 'foods', e.target.value)}
              placeholder="What did you eat for dinner?"
            />
          </FormGroup>
          
          <Grid>
            <FormGroup>
              <Label>Estimated Calories</Label>
              <Input
                type="number"
                value={progressData.meals.dinner.calories}
                onChange={(e) => handleMealChange('dinner', 'calories', e.target.value)}
                placeholder="Calories"
                min="0"
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Notes</Label>
              <Input
                type="text"
                value={progressData.meals.dinner.notes}
                onChange={(e) => handleMealChange('dinner', 'notes', e.target.value)}
                placeholder="Any notes?"
              />
            </FormGroup>
          </Grid>
        </MealSection>
        
        <div style={{ 
          background: '#f8f9fa', 
          padding: '1.5rem', 
          borderRadius: '8px', 
          marginTop: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <CardIcon color="#dc3545" bgColor="rgba(220, 53, 69, 0.1)">
              <FaChartLine />
            </CardIcon>
            <span style={{ fontWeight: 600, fontSize: '1.2rem' }}>Total Calories</span>
          </div>
          <span style={{ 
            fontSize: '1.5rem', 
            fontWeight: 700,
            color: progressData.totalCalories > 2000 ? '#dc3545' : '#28a745'
          }}>
            {progressData.totalCalories}
          </span>
        </div>
      </Card>

      {/* Additional Tracking */}
      <Card>
        <CardHeader>
          <CardIcon color="#6c63ff" bgColor="rgba(108, 99, 255, 0.1)">
            <FaNotesMedical />
          </CardIcon>
          <CardTitle>Additional Info</CardTitle>
        </CardHeader>
        
        <Grid>
          <FormGroup>
            <Label htmlFor="water">
              <FaTint size={14} style={{ color: '#0dcaf0' }} />
              Water Intake
            </Label>
            <Input
              type="text"
              id="water"
              value={progressData.waterIntake}
              onChange={(e) => handleInputChange('waterIntake', e.target.value)}
              placeholder="e.g., 8 glasses or 2 liters"
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="notes">Daily Notes</Label>
            <TextArea
              id="notes"
              value={progressData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="How did you feel today? Any observations?"
            />
          </FormGroup>
        </Grid>
      </Card>

      {success && (
        <SuccessMessage>
          <FaSave />
          {success}
        </SuccessMessage>
      )}
      
      {error && (
        <ErrorMessage>
          <FaWeight />
          {error}
        </ErrorMessage>
      )}

      <Button onClick={handleSubmit} disabled={loading}>
        <FaSave />
        {loading ? 'Saving...' : 'Save Daily Progress'}
      </Button>
    </PageContainer>
  );
};

export default DailyProgressTracker;